import { useState, useEffect, useRef, useCallback, RefObject } from 'react'

import { Col, Row, Tab, Tooltip } from '@enuygun/design-system'
import { i18n, useTranslation } from 'next-i18next'

import NextImage from 'components/Image'
import SnapCarousel from 'components/SnapCarousel'
import { useCurrency } from 'hooks/useCurrency'
import { useIsMobile } from 'hooks/useIsMobile'
import { useDeviceType } from 'hooks/useDeviceType'
import { useAppState } from 'context/AppContext'
import { InfoCircleOutline } from '@enuygun/icons'
import { IconWrapper, TooltipText } from 'components/styled'
import * as S from './DiscoverWingie.styled'
import { DiscoverCardCategory, DiscoverCardImageLink, DiscoverWrapper } from '../Discover/Discover.styled'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

const formatDiscoverPrice = (html: string) => {
  // Tag strip without backtracking regex (ReDoS-safe)
  let withoutTags = ''
  let index = 0
  while (index < html.length) {
    if (html[index] === '<') {
      const close = html.indexOf('>', index + 1)
      if (close === -1) {
        withoutTags += html.slice(index)
        break
      }
      index = close + 1
      continue
    }
    withoutTags += html[index]
    index += 1
  }

  return withoutTags
    .replaceAll('&nbsp;', ' ')
    .replaceAll('\u00a0', ' ')
    .replaceAll(/&[a-z]{1,32};/gi, '')
    .trim()
}

const LazyDiscoverWingieSectionDesktop: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsClient(true)

    if (globalThis.window === undefined || !('IntersectionObserver' in globalThis.window)) {
      setIsVisible(true)
      return
    }

    const currentElement = sectionRef.current
    if (!currentElement) return

    setIsVisible(false)

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(currentElement)
        }
      },
      {
        rootMargin: '300px',
        threshold: 0.01,
      }
    )

    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      }
    }, 250)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [])

  const shouldShowContent = !isClient || isVisible

  return (
    <>
      <style jsx>{`
        @keyframes wingieSpin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }
        .wingie-spinner {
          animation: wingieSpin 1s linear infinite;
        }
      `}</style>

      <div
        ref={sectionRef}
        data-testid="lazy-discover-wingie-section-desktop"
        data-visible={shouldShowContent ? 'true' : 'false'}
        style={{
          minHeight: shouldShowContent ? 'auto' : '450px',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        {shouldShowContent ? (
          children
        ) : (
          <div
            style={{
              width: '100%',
              height: '450px',
              backgroundColor: '#fff8e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              border: '1px solid #ffe0b2',
              margin: '20px 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <div
                className="wingie-spinner"
                style={{
                  width: '38px',
                  height: '38px',
                  border: '3px solid #ffe0b2',
                  borderTop: '3px solid #ff9800',
                  borderRadius: '50%',
                }}
              />
              <div
                style={{
                  color: '#ff9800',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                ...
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

const LazyDiscoverWingieCardMobile: React.FC<{
  children: React.ReactNode
  index: number
}> = ({ children, index }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isClient, setIsClient] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setIsClient(true)

    if (globalThis.window === undefined || !('IntersectionObserver' in globalThis.window)) {
      setIsVisible(true)
      return
    }

    const currentElement = cardRef.current
    if (!currentElement) return

    setIsVisible(false)

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true)
          observer.unobserve(currentElement)
        }
      },
      {
        rootMargin: '50px',
        threshold: 0.2,
      }
    )

    const timeoutId = setTimeout(() => {
      if (currentElement) {
        observer.observe(currentElement)
      }
    }, 50)

    return () => {
      clearTimeout(timeoutId)
      observer.disconnect()
    }
  }, [])

  const shouldShowContent = !isClient || isVisible

  return (
    <div
      ref={cardRef}
      data-testid={`lazy-discover-wingie-card-mobile-${index}`}
      data-visible={shouldShowContent ? 'true' : 'false'}
      style={{
        minHeight: shouldShowContent ? 'auto' : '220px',
        transition: 'all 0.3s ease-in-out',
      }}
    >
      {shouldShowContent ? (
        children
      ) : (
        <div
          style={{
            width: '100%',
            height: '220px',
            backgroundColor: '#fff8e1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '12px',
            border: '1px solid #ffe0b2',
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              border: '2px solid #ffe0b2',
              borderTop: '2px solid #ff9800',
              borderRadius: '50%',
              animation: 'wingieSpin 1s linear infinite',
            }}
          />
        </div>
      )}
    </div>
  )
}

interface DiscoverWingieTabContentProps {
  data: any[]
  tabType: string
  isMobile: boolean
  isTablet: boolean
  isRTL: boolean
  currencyFormatter: ReturnType<typeof useCurrency>
  currency: { iso?: string } | undefined
  domainName: string
  t: (key: string, options?: any) => string
  onCardClick: (origin: string, destination: string, url: string, order: number, tabType: string, price: number | null, formattedPrice: string | null) => void
}

const DiscoverWingieTabContent: React.FC<DiscoverWingieTabContentProps> = ({
  data,
  tabType,
  isMobile,
  isTablet,
  isRTL,
  currencyFormatter,
  currency,
  domainName,
  t,
  onCardClick,
}) => (
  <>
    {!isMobile && !isTablet ? (
      <Row gutter={[8, 16]}>
        {data?.map(({ image, url, price, ...rest }, i) => {
          const origin = rest.origin.name
          const destination = rest.destination.name
          const formattedPrice = price && currencyFormatter?.conversion ? formatDiscoverPrice(currencyFormatter.summaryFormatHtml(price, 0, false, true, currency?.iso)) : null
          return (
            <Col key={url} lg={3} md={3} data-testid={`wingie-homepage-routesBox-${i}`}>
              <S.DiscoverCard
                href={url}
                title={t('popularRoutesImage', { origin, destination })}
                data-testid={`${domainName}-homepage-routesBox-${i}-card`}
                onClick={() => onCardClick(origin, destination, url, i + 1, tabType, price ?? null, formattedPrice)}
              >
                <DiscoverCardImageLink>
                  <NextImage unoptimized src={image} fill alt={t('popularRoutesImage', { origin, destination })} loading="lazy" />
                </DiscoverCardImageLink>
                <DiscoverCardCategory>
                  {price && currencyFormatter?.conversion && (
                    <S.DiscoverCardTitle data-testid={`wingie-homepage-prices-${i}`}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('DiscoverWingie', {
                            currency: `<div style="display: inline-block;">${currencyFormatter.summaryFormatHtml(price, 0, false, true, currency?.iso)}</div>`,
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      />
                    </S.DiscoverCardTitle>
                  )}
                  <S.DiscoverCardElementWrapper>
                    <S.DiscoverCardCategoryElement>{t('popularRoutes', { origin, destination })}</S.DiscoverCardCategoryElement>
                  </S.DiscoverCardElementWrapper>
                </DiscoverCardCategory>
              </S.DiscoverCard>
            </Col>
          )
        })}
      </Row>
    ) : (
      <SnapCarousel
        isRTL={isRTL}
        options={{
          perPage: isTablet ? 2.4 : 1.36,
          gap: 16,
        }}
        dataTestId="discover-wingie-carousel"
        containerWidth={1120}
        isMobile={!!isMobile}
      >
        {data?.map(({ image, url, price, ...rest }, index) => {
          const origin = rest.origin.name
          const destination = rest.destination.name
          const formattedPrice = price && currencyFormatter?.conversion ? formatDiscoverPrice(currencyFormatter.summaryFormatHtml(price, 0, false, true, currency?.iso)) : null
          return (
            <LazyDiscoverWingieCardMobile key={url} index={index}>
              <S.DiscoverCard
                href={url}
                title={t('popularRoutesImage', { origin, destination })}
                data-testid={`${domainName}-homepage-routesBox-${index}-card`}
                onClick={() => onCardClick(origin, destination, url, index + 1, tabType, price ?? null, formattedPrice)}
              >
                <NextImage
                  src={image}
                  width={200}
                  height={200}
                  alt={t('popularRoutesImage', { origin, destination })}
                  data-testid={`${domainName}-homepage-routesBox-${index}-image`}
                />
                <S.DiscoverCardCategory data-testid={`${domainName}-homepage-routesBox-${index}-category`}>
                  {price && currencyFormatter?.conversion && (
                    <S.DiscoverCardTitle data-testid={`${domainName}-homepage-prices-${index}`}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: t('DiscoverWingie', {
                            currency: `<div style="display: inline-block;">${currencyFormatter.summaryFormatHtml(price, 0, false, true, currency?.iso)}</div>`,
                            interpolation: { escapeValue: false },
                          }),
                        }}
                      />
                    </S.DiscoverCardTitle>
                  )}
                  <S.DiscoverCardElementWrapper>
                    <S.DiscoverCardCategoryElement data-testid={`${domainName}-homepage-routesBox-${index}-category-label`}>
                      {t('popularRoutes', { origin, destination })}
                    </S.DiscoverCardCategoryElement>
                  </S.DiscoverCardElementWrapper>
                </S.DiscoverCardCategory>
              </S.DiscoverCard>
            </LazyDiscoverWingieCardMobile>
          )
        })}
      </SnapCarousel>
    )}
  </>
)

interface DiscoverWingieContentProps {
  containerRef: RefObject<HTMLDivElement>
  domainName: string
  tooltipText: string | false
  isMobile: boolean
  isTablet: boolean
  isRTL: boolean
  currency: { iso?: string } | undefined
  discoverWingieData: any
  hasInternational: boolean
  hasDomestic: boolean
  internationalFlights: any[]
  domesticFlights: any[]
  currencyFormatter: ReturnType<typeof useCurrency>
  t: (key: string, options?: any) => string
  onCardClick: DiscoverWingieTabContentProps['onCardClick']
}

const DiscoverWingieContent: React.FC<DiscoverWingieContentProps> = ({
  containerRef,
  domainName,
  tooltipText,
  isMobile,
  isTablet,
  isRTL,
  currency,
  discoverWingieData,
  hasInternational,
  hasDomestic,
  internationalFlights,
  domesticFlights,
  currencyFormatter,
  t,
  onCardClick,
}) => (
  <div ref={containerRef}>
    <S.DiscoverHeading data-testid={`${domainName}-homepage-discover-heading`}>
      {t('discover.heading')}
      {tooltipText ? (
        <span style={{ display: 'inline-block' }} data-testid={`${domainName}-homepage-discover-heading-tooltip-span`}>
          <Tooltip
            bottomSheet={true}
            title={<div></div>}
            popover={{ width: '350px', popover: { placement: 'bottom' } }}
            button={
              <IconWrapper bgColor={isMobile || isTablet ? '#F8FAF9' : '#fff'} data-testid={`${domainName}-homepage-discover-heading-tooltip-button`}>
                <InfoCircleOutline width={15.6} />
              </IconWrapper>
            }
          >
            <TooltipText data-testid={`${domainName}-homepage-discover-heading-tooltip-text`}>{tooltipText}</TooltipText>
          </Tooltip>
        </span>
      ) : null}
    </S.DiscoverHeading>
    <DiscoverWrapper>
      <Tab
        dataTestId="routesButton"
        shape="outlined"
        key={currency?.iso}
        // @ts-ignore
      >
        {[
          hasInternational && (
            /* @ts-ignore*/
            <Tab.Panel unmount={false} title={discoverWingieData[0]?.PopularGlobalRoutes?.title} panelKey={0} key={0} data-testid={`${domainName}-homepage-routesBox-tab-panel-0`}>
              <DiscoverWingieTabContent
                data={internationalFlights}
                tabType="international"
                isMobile={!!isMobile}
                isTablet={!!isTablet}
                isRTL={isRTL}
                currencyFormatter={currencyFormatter}
                currency={currency}
                domainName={domainName}
                t={t}
                onCardClick={onCardClick}
              />
            </Tab.Panel>
          ),
          hasDomestic && (
            /* @ts-ignore*/
            <Tab.Panel
              unmount={false}
              title={discoverWingieData[1]?.PopularDomesticRoutes?.title}
              panelKey={1}
              key={1}
              data-testid={`${domainName}-homepage-routesBox-tab-panel-1`}
            >
              <DiscoverWingieTabContent
                data={domesticFlights}
                tabType="domestic"
                isMobile={!!isMobile}
                isTablet={!!isTablet}
                isRTL={isRTL}
                currencyFormatter={currencyFormatter}
                currency={currency}
                domainName={domainName}
                t={t}
                onCardClick={onCardClick}
              />
            </Tab.Panel>
          ),
        ].filter(Boolean)}
      </Tab>
    </DiscoverWrapper>
  </div>
)

const DiscoverWingie = ({ discoverWingieData, res }) => {
  const currencyFormatter = useCurrency()
  const { currency, market } = useAppState()
  const { domain } = useAppState()
  const domainName = domain === 'tr' ? 'enuygun' : 'wingie'
  const isMobile = useIsMobile()
  const discoverWingieContainerRef = useRef<HTMLDivElement>(null)
  const { isTablet } = useDeviceType()

  const { t } = useTranslation('common')

  const tooltipText = t('discover.headingTooltip', { defaultValue: false })

  // DiscoverWingie card click tracking
  const handleDiscoverWingieCardClick = useCallback(
    (origin: string, destination: string, url: string, order: number, tabType: string, price: number | null, formattedPrice: string | null) => {
      const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: 'wingie-homepage',
        page: 'wingie-homepage',
        type: 'click',
        message: 'discover_wingie_card_click',
        environment: environment,
        requestId,
        origin: origin,
        destination: destination,
        linkUrl: url,
        cardOrder: order,
        tabType: tabType,
        price: formattedPrice ?? null,
        isMobile: !!isMobile,
        market: market,
      })
    },
    [isMobile, market]
  )

  const internationalFlights = res?.[3]?.featuredFlights?.international
  const domesticFlights = res?.[3]?.featuredFlights?.domestic
  const hasInternational = (internationalFlights?.length ?? 0) > 0
  const hasDomestic = (domesticFlights?.length ?? 0) > 0

  if (!discoverWingieData || discoverWingieData.length === 0 || !res?.[3]?.featuredFlights || (!hasInternational && !hasDomestic)) {
    return null
  }

  const isRTL = i18n?.dir(i18n?.language?.includes('-') ? i18n.language.split('-')[1] : i18n?.language) === 'rtl'

  const content = (
    <DiscoverWingieContent
      containerRef={discoverWingieContainerRef}
      domainName={domainName}
      tooltipText={tooltipText}
      isMobile={!!isMobile}
      isTablet={!!isTablet}
      isRTL={isRTL}
      currency={currency}
      discoverWingieData={discoverWingieData}
      hasInternational={hasInternational}
      hasDomestic={hasDomestic}
      internationalFlights={internationalFlights}
      domesticFlights={domesticFlights}
      currencyFormatter={currencyFormatter}
      t={t}
      onCardClick={handleDiscoverWingieCardClick}
    />
  )

  return isMobile || isTablet ? content : <LazyDiscoverWingieSectionDesktop data-testid={`${domainName}-homepage-routesBox-desktop`}>{content}</LazyDiscoverWingieSectionDesktop>
}

export default DiscoverWingie
